import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LogOut, User, PlusCircle, Compass, Search, Heart } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ onSearchToggle, searchOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExplore = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/random`, {
        withCredentials: true
      });
      navigate(`/wiki/${data.id}`);
    } catch (err) {
      navigate('/wiki');
    }
  };

  return (
    <header className="sticky top-0 bg-background/95 backdrop-blur border-b border-border z-50" data-testid="main-header">
      <div className="container mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="header-home-link">
            <img 
              src="/images/griffon-sign-logo.png" 
              alt="Growling Griffon Logo" 
              className="h-24 w-auto object-contain hover:opacity-80 transition-opacity duration-300"
              data-testid="header-sign-logo"
            />
          </Link>
          <Link to="/wiki/the-growling-griffon" className="group" data-testid="header-logo-link">
            <span className="text-3xl lg:text-4xl font-serif font-bold text-foreground group-hover:text-primary transition-colors duration-300">
              The Growling Griffon
            </span>
          </Link>
        </div>
        
        <nav className="flex items-center gap-4">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="lg"
                  className="font-sans text-lg gap-2"
                  onClick={handleExplore}
                  data-testid="header-explore-button"
                >
                  <Compass className="w-5 h-5" aria-hidden="true" />
                  Explore
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-amber-50 text-stone-900 border-amber-900/40 shadow-lg">
                <p className="font-sans text-sm font-medium">Random Glossary Entry</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button
            variant={searchOpen ? "secondary" : "ghost"}
            size="lg"
            className="font-sans text-lg gap-2"
            onClick={onSearchToggle}
            data-testid="header-search-button"
          >
            <Search className="w-5 h-5" aria-hidden="true" />
            Search
          </Button>

          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href="https://www.patreon.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="header-support-link"
                >
                  <Button
                    variant="ghost"
                    size="lg"
                    className="font-sans text-lg gap-2 text-primary hover:text-primary"
                    data-testid="header-support-button"
                  >
                    <Heart className="w-5 h-5" aria-hidden="true" />
                    Support
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-amber-50 text-stone-900 border-amber-900/40 shadow-lg">
                <p className="font-sans text-sm font-medium">Support us on Patreon</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {user ? (
            <>
              <Link to="/wiki/new" data-testid="header-create-entry-link">
                <Button variant="outline" size="lg" className="font-sans gap-2 text-lg">
                  <PlusCircle className="w-5 h-5" aria-hidden="true" />
                  New Entry
                </Button>
              </Link>
              <div className="flex items-center gap-3 ml-2">
                <div className="flex items-center gap-2 px-4 py-3 rounded-sm bg-card border border-border" data-testid="user-info-display">
                  <User className="w-5 h-5 text-primary" aria-hidden="true" />
                  <span className="text-base font-sans text-foreground">{user.name}</span>
                </div>
                <Button variant="ghost" size="lg" onClick={handleLogout} data-testid="header-logout-button">
                  <LogOut className="w-5 h-5" aria-hidden="true" />
                </Button>
              </div>
            </>
          ) : (
            <Link to="/login" data-testid="header-login-link">
              <Button variant="outline" size="lg" className="font-sans text-lg">
                Log In
              </Button>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
